import {
  AccountCircle,
  Add,
  Business,
  CorporateFare,
  Delete,
  Download,
  Edit,
  Group,
  MoreVert,
  Pause,
  Person,
  PersonRemove,
  PlayArrow,
  Search,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import CompanyFormDialog from "./dialogs/CompanyFormDialog";
import CompanyUsersDialog from "./dialogs/CompanyUsersDialog";
import GenerateReportDialog from "./dialogs/GenerateReportDialog";
import OrganizationFormDialog, {
  OrganizationData,
} from "./dialogs/OrganizationFormDialog";
import RemoveCustomerDialog from "./dialogs/RemoveCustomerDialog";
import WhitelabelCustomerDetailDialog from "./WhitelabelCustomerDetailDialog";

interface CompanyInfo {
  id: string;
  name: string;
  industry?: string;
  size?: string;
  location?: string;
  website?: string;
  description?: string;
  role: string;
}

interface WhitelabelCustomer {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  created_date?: string;
  last_login?: string;
  referral_source?: string;
  primary_company?: CompanyInfo;
  companies: Array<CompanyInfo>;
  usage: {
    total_credits: number;
    total_tokens: number;
    total_requests: number;
    recent_credits: number;
    recent_requests: number;
  };
  subscription: {
    plan?: string;
    whitelabel: boolean;
    whitelabelLimit: number;
    creditLimit?: number;
    seats?: number;
    provisioned_by_whitelabel?: boolean;
  };
  in_organization?: boolean;
}

interface WhitelabelCustomersResponse {
  customers: WhitelabelCustomer[];
  total: number;
  page: number;
  pageSize: number;
  whitelabel: {
    id: string;
    domain?: string;
    title: string;
  };
}

interface ProvisionedCompany {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  size?: string;
  location?: string;
  website?: string;
  logoUrl?: string;
  allowedFeatures?: string[] | null;
  features: Record<string, string>;
  creditLimit: number | null;
  creditUsage: number;
  zeroDataRetention?: boolean;
  mfaRequired?: boolean;
  seats: number | null;
  status: string;
  organization_id?: string | null;
  organizationName?: string | null;
  owner: { uid: string; email: string; role: string } | null;
  userCount: number;
  users: Array<{ uid: string; email: string; role: string }>;
  created_date: string | null;
}

interface ProvisionedOrganization {
  id: string;
  name: string;
  description?: string;
  features: Record<string, string>;
  creditLimit: number | null;
  seats: number | null;
  companyLimit: number | null;
  status: string;
  ownerUid?: string;
  owner?: { uid: string; email: string; displayName?: string } | null;
  owners?: Array<{ uid: string; email: string; displayName?: string }>;
  companyCount: number;
  companies: Array<{ id: string; name: string }>;
  created_date: string | null;
}

export default function WhitelabelCustomers({
  currentUser,
  getToken,
  whitelabelId,
  companyProvisioningEnabled,
}: {
  currentUser: User;
  getToken: Function;
  whitelabelId: string;
  companyProvisioningEnabled: boolean;
}) {
  const [currentTab, setCurrentTab] = useState(0);

  // Customer state
  const [customers, setCustomers] = useState<WhitelabelCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [whitelabelInfo, setWhitelabelInfo] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] =
    useState<WhitelabelCustomer | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [customerToRemove, setCustomerToRemove] =
    useState<WhitelabelCustomer | null>(null);

  // Company state
  const [companies, setCompanies] = useState<ProvisionedCompany[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesRefreshing, setCompaniesRefreshing] = useState(false);
  const [companiesPage, setCompaniesPage] = useState(0);
  const [companiesPageSize, setCompaniesPageSize] = useState(25);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [companiesSearch, setCompaniesSearch] = useState("");
  const [companyFormOpen, setCompanyFormOpen] = useState(false);
  const [editCompany, setEditCompany] = useState<any>(null);
  const [companyAnchorEl, setCompanyAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedCompany, setSelectedCompany] =
    useState<ProvisionedCompany | null>(null);
  const [companyUsersDialogOpen, setCompanyUsersDialogOpen] = useState(false);
  const [companyForUsersDialog, setCompanyForUsersDialog] =
    useState<ProvisionedCompany | null>(null);

  // Organization state
  const [organizations, setOrganizations] = useState<ProvisionedOrganization[]>(
    []
  );
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsRefreshing, setOrgsRefreshing] = useState(false);
  const [orgsPage, setOrgsPage] = useState(0);
  const [orgsPageSize, setOrgsPageSize] = useState(25);
  const [orgsTotal, setOrgsTotal] = useState(0);
  const [orgsSearch, setOrgsSearch] = useState("");
  const [orgFormOpen, setOrgFormOpen] = useState(false);
  const [editOrg, setEditOrg] = useState<OrganizationData | null>(null);
  const [orgAnchorEl, setOrgAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrg, setSelectedOrg] =
    useState<ProvisionedOrganization | null>(null);

  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Helper function to determine if a customer was provisioned by the whitelabeler
  const isProvisionedCustomer = (customer: WhitelabelCustomer): boolean => {
    return customer.subscription?.provisioned_by_whitelabel === true;
  };

  // Helper function to get display name for plan
  const getPlanDisplayName = (customer: WhitelabelCustomer): string => {
    const plan = customer.subscription?.plan;
    if (!plan) return "Free";

    const planLower = plan.toLowerCase();
    switch (planLower) {
      case "free":
        return "Free";
      case "entrepreneur":
        return "Entrepreneur";
      case "business":
        return "Business";
      case "enterprise":
        return "Enterprise";
      default:
        // For custom/whitelabel plans, format the plan name nicely
        // e.g., "small-agency-usib" -> "Small Agency Usib"
        return plan
          .split(/[-_]/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadCustomers = async () => {
    if (refreshing) return;
    setRefreshing(true);

    const currentToken = await getToken();

    try {
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/customers`,
        {
          params: {
            page: page + 1,
            pageSize,
            search: search.trim() || undefined,
          },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      const data = response.data as WhitelabelCustomersResponse;
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
      setWhitelabelInfo(data.whitelabel);
    } catch (err) {
      console.error("Error loading customers:", err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 403) {
          console.error(
            "Access denied - user is not authorized to manage this whitelabel instance"
          );
        } else if (err.response?.status === 404) {
          console.error("Whitelabel instance not found");
        }
      }
    } finally {
      setRefreshing(false);
    }
  };

  const loadCompanies = async () => {
    if (companiesRefreshing) return;
    setCompaniesRefreshing(true);

    const currentToken = await getToken();

    try {
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/companies`,
        {
          params: {
            page: companiesPage + 1,
            pageSize: companiesPageSize,
            search: companiesSearch.trim() || undefined,
          },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setCompanies(response.data.companies || []);
      setCompaniesTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error loading companies:", err);
    } finally {
      setCompaniesRefreshing(false);
    }
  };

  const loadOrganizations = async () => {
    if (orgsRefreshing) return;
    setOrgsRefreshing(true);

    const currentToken = await getToken();

    try {
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/organizations`,
        {
          params: {
            page: orgsPage + 1,
            pageSize: orgsPageSize,
            search: orgsSearch.trim() || undefined,
          },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setOrganizations(response.data.organizations || []);
      setOrgsTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error loading organizations:", err);
    } finally {
      setOrgsRefreshing(false);
    }
  };

  useEffect(() => {
    if (currentUser.uid && whitelabelId) {
      // When company provisioning is disabled, only show users tab
      if (!companyProvisioningEnabled) {
        setLoading(true);
        loadCustomers().finally(() => setLoading(false));
      } else if (currentTab === 0) {
        setOrgsLoading(true);
        loadOrganizations().finally(() => setOrgsLoading(false));
      } else if (currentTab === 1) {
        setCompaniesLoading(true);
        Promise.all([
          loadCompanies(),
          organizations.length === 0 ? loadOrganizations() : Promise.resolve(),
        ]).finally(() => setCompaniesLoading(false));
      } else if (currentTab === 2) {
        setLoading(true);
        loadCustomers().finally(() => setLoading(false));
      }
    }
  }, [currentUser.uid, whitelabelId, page, pageSize, currentTab, companiesPage, companiesPageSize, orgsPage, orgsPageSize, companyProvisioningEnabled]);

  // Debounced search for organizations
  useEffect(() => {
    if (currentTab !== 0) return;
    const timer = setTimeout(() => {
      if (currentUser.uid && whitelabelId) {
        setOrgsPage(0);
        loadOrganizations();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [orgsSearch]);

  // Debounced search for companies
  useEffect(() => {
    if (currentTab !== 1) return;
    const timer = setTimeout(() => {
      if (currentUser.uid && whitelabelId) {
        setCompaniesPage(0);
        loadCompanies();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companiesSearch]);

  // Debounced search for customers
  useEffect(() => {
    // Run search when on users tab OR when company provisioning is disabled
    if (currentTab !== 2 && companyProvisioningEnabled) return;
    const timer = setTimeout(() => {
      if (currentUser.uid && whitelabelId) {
        setPage(0);
        loadCustomers();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    customer: WhitelabelCustomer
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomer(null);
  };

  const handleViewDetails = () => {
    setDetailDialogOpen(true);
    setAnchorEl(null);
  };

  const handleRemoveCustomer = () => {
    if (!selectedCustomer) return;
    setCustomerToRemove(selectedCustomer);
    setRemoveDialogOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmRemove = async () => {
    if (!customerToRemove) return;

    setRemoveLoading(true);

    try {
      const currentToken = await getToken();

      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/customers/${customerToRemove.id}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      loadCustomers();
    } catch (err: any) {
      console.error("Error removing customer:", err);
    } finally {
      setRemoveLoading(false);
      setRemoveDialogOpen(false);
      setCustomerToRemove(null);
      setSelectedCustomer(null);
    }
  };

  const handleCancelRemove = () => {
    setRemoveDialogOpen(false);
    setCustomerToRemove(null);
    setSelectedCustomer(null);
  };

  // Company actions
  const handleCompanyMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    company: ProvisionedCompany
  ) => {
    event.stopPropagation();
    setCompanyAnchorEl(event.currentTarget);
    setSelectedCompany(company);
  };

  const handleCompanyMenuClose = () => {
    setCompanyAnchorEl(null);
    setSelectedCompany(null);
  };

  const handleEditCompany = () => {
    if (!selectedCompany) return;
    setEditCompany(selectedCompany);
    setCompanyFormOpen(true);
    setCompanyAnchorEl(null);
  };

  const handleToggleCompanyStatus = async () => {
    if (!selectedCompany) return;
    const newStatus =
      selectedCompany.status === "active" ? "suspended" : "active";

    try {
      const currentToken = await getToken();
      await axios.put(
        `${hostname}/whitelabel/${whitelabelId}/companies/${selectedCompany.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      loadCompanies();
    } catch (err) {
      console.error("Error toggling company status:", err);
    }
    setCompanyAnchorEl(null);
    setSelectedCompany(null);
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;

    try {
      const currentToken = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/companies/${selectedCompany.id}`,
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      loadCompanies();
    } catch (err) {
      console.error("Error deleting company:", err);
    }
    setCompanyAnchorEl(null);
    setSelectedCompany(null);
  };

  const handleManageCompanyUsers = () => {
    if (!selectedCompany) return;
    setCompanyForUsersDialog(selectedCompany);
    setCompanyUsersDialogOpen(true);
    setCompanyAnchorEl(null);
  };

  // Organization actions
  const handleOrgMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    org: ProvisionedOrganization
  ) => {
    event.stopPropagation();
    setOrgAnchorEl(event.currentTarget);
    setSelectedOrg(org);
  };

  const handleOrgMenuClose = () => {
    setOrgAnchorEl(null);
    setSelectedOrg(null);
  };

  const handleEditOrg = () => {
    if (!selectedOrg) return;
    setEditOrg(selectedOrg as OrganizationData);
    setOrgFormOpen(true);
    setOrgAnchorEl(null);
  };

  const handleToggleOrgStatus = async () => {
    if (!selectedOrg) return;
    const newStatus =
      selectedOrg.status === "active" ? "suspended" : "active";

    try {
      const currentToken = await getToken();
      await axios.put(
        `${hostname}/whitelabel/${whitelabelId}/organizations/${selectedOrg.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      loadOrganizations();
    } catch (err) {
      console.error("Error toggling org status:", err);
    }
    setOrgAnchorEl(null);
    setSelectedOrg(null);
  };

  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;

    try {
      const currentToken = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/organizations/${selectedOrg.id}`,
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      loadOrganizations();
    } catch (err) {
      console.error("Error deleting org:", err);
    }
    setOrgAnchorEl(null);
    setSelectedOrg(null);
  };

  const getEnabledFeatureCount = (features: Record<string, string>): number => {
    if (!features) return 0;
    return Object.values(features).filter((s) => s === "enabled").length;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Customer Management
          </Typography>
          {whitelabelInfo && (
            <Typography variant="body2" color="text.secondary">
              {whitelabelInfo.title} ({whitelabelInfo.domain || "No domain"})
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => setReportDialogOpen(true)}
        >
          Generate Report
        </Button>
      </Box>

      {companyProvisioningEnabled ? (
        <Tabs
          value={currentTab}
          onChange={(_, v) => setCurrentTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          <Tab label="Organizations" />
          <Tab label="Companies" />
          <Tab label="Users" />
        </Tabs>
      ) : null}

      {/* Show loading indicator below tabs */}
      {(loading || companiesLoading || orgsLoading) && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      {/* Users Tab - shown when currentTab is 2 OR when company provisioning is disabled */}
      {(currentTab === 2 || !companyProvisioningEnabled) && !loading && (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search customers by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          <Paper variant="outlined">
            {refreshing && <LinearProgress />}
            {!refreshing && <div style={{ height: 4 }}></div>}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Referral Source</TableCell>
                    <TableCell>Company Info</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Total Usage</TableCell>
                    <TableCell>Recent Activity</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            src={customer.photoURL}
                            sx={{ width: 32, height: 32 }}
                          >
                            <AccountCircle />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {customer.displayName || customer.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {customer.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {customer.referral_source ? (
                          <Chip
                            label={customer.referral_source}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.primary_company ? (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Business fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="medium">
                                {customer.primary_company.name}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {customer.primary_company.industry && (
                                <Tooltip title="Industry">
                                  <Chip
                                    label={customer.primary_company.industry}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: "0.7rem" }}
                                  />
                                </Tooltip>
                              )}
                              {customer.primary_company.size && (
                                <Tooltip title="Company Size">
                                  <Chip
                                    label={customer.primary_company.size}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: "0.7rem" }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            {customer.primary_company.location && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                {customer.primary_company.location}
                              </Typography>
                            )}
                            {customer.companies.length > 1 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                +{customer.companies.length - 1} more companies
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No company
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.in_organization ? (
                          <Chip
                            label="Org"
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        ) : (
                          <Chip
                            label={getPlanDisplayName(customer)}
                            size="small"
                            color={
                              getPlanDisplayName(customer) !== "Free"
                                ? "primary"
                                : "default"
                            }
                            variant={
                              getPlanDisplayName(customer) !== "Free"
                                ? "filled"
                                : "outlined"
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {customer.usage.total_credits.toLocaleString()}{" "}
                            credits
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {customer.usage.total_requests.toLocaleString()}{" "}
                            requests
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {customer.usage.recent_credits.toLocaleString()}{" "}
                            credits
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {customer.usage.recent_requests.toLocaleString()}{" "}
                            requests (30d)
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {customer.created_date
                            ? moment(customer.created_date).format(
                                "MMM Do YYYY"
                              )
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {customer.last_login
                            ? moment(customer.last_login).fromNow()
                            : "Never"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, customer)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {customers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 4 }}>
                          <Person
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" color="text.secondary">
                            No customers found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {search
                              ? "Try adjusting your search terms"
                              : "Customers will appear here once they sign up through your whitelabel instance"}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {total > pageSize && (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={total}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Organizations Tab */}
      {currentTab === 0 && companyProvisioningEnabled && !orgsLoading && (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search organizations..."
              value={orgsSearch}
              onChange={(e) => setOrgsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setEditOrg(null);
                setOrgFormOpen(true);
              }}
            >
              New Organization
            </Button>
          </Box>

          <Paper variant="outlined">
            {orgsRefreshing && <LinearProgress />}
            {!orgsRefreshing && <div style={{ height: 4 }}></div>}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Companies</TableCell>
                    <TableCell>Features</TableCell>
                    <TableCell>Credits</TableCell>
                    <TableCell>Seats</TableCell>
                    <TableCell>Company Limit</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organizations.map((org) => (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CorporateFare fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {org.name}
                            </Typography>
                            {org.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {org.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {org.owner ? (
                          <Box>
                            {org.owner.displayName && (
                              <Typography variant="body2">
                                {org.owner.displayName}
                              </Typography>
                            )}
                            <Typography
                              variant={org.owner.displayName ? "caption" : "body2"}
                              color={org.owner.displayName ? "text.secondary" : "text.primary"}
                            >
                              {org.owner.email}
                            </Typography>
                            {(org.owners?.length ?? 0) > 1 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                +{(org.owners?.length ?? 1) - 1} more
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {org.companyCount}{" "}
                          {org.companyCount === 1 ? "company" : "companies"}
                        </Typography>
                        {org.companies.length > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {org.companies
                              .slice(0, 3)
                              .map((c) => c.name)
                              .join(", ")}
                            {org.companies.length > 3
                              ? ` +${org.companies.length - 3} more`
                              : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${getEnabledFeatureCount(org.features)} enabled`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {org.creditLimit != null
                            ? Number(org.creditLimit).toLocaleString()
                            : "Unlimited"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {org.seats != null ? org.seats : "Unlimited"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {org.companyLimit != null ? org.companyLimit : "Unlimited"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={org.status || "active"}
                          size="small"
                          color={
                            org.status === "suspended" ? "default" : "primary"
                          }
                          variant={
                            org.status === "suspended" ? "outlined" : "filled"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {org.created_date
                            ? moment(org.created_date).format("MMM Do YYYY")
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleOrgMenuClick(e, org)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {organizations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Box sx={{ py: 4 }}>
                          <CorporateFare
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" color="text.secondary">
                            No organizations found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {orgsSearch
                              ? "Try adjusting your search terms"
                              : 'Create an organization using the "New Organization" button above'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {orgsTotal > orgsPageSize && (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={orgsTotal}
                  rowsPerPage={orgsPageSize}
                  page={orgsPage}
                  onPageChange={(_, p) => setOrgsPage(p)}
                  onRowsPerPageChange={(e) => {
                    setOrgsPageSize(parseInt(e.target.value, 10));
                    setOrgsPage(0);
                  }}
                />
              )}
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Companies Tab */}
      {currentTab === 1 && companyProvisioningEnabled && !companiesLoading && (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search companies..."
              value={companiesSearch}
              onChange={(e) => setCompaniesSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setEditCompany(null);
                setCompanyFormOpen(true);
              }}
            >
              New Company
            </Button>
          </Box>

          <Paper variant="outlined">
            {companiesRefreshing && <LinearProgress />}
            {!companiesRefreshing && <div style={{ height: 4 }}></div>}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Features</TableCell>
                    <TableCell>Credits</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Business fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight="medium">
                            {company.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {company.organizationName ? (
                          <Chip
                            icon={<CorporateFare />}
                            label={company.organizationName}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.owner?.email || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${getEnabledFeatureCount(company.features)} enabled`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {company.creditLimit != null
                              ? `${Number(company.creditUsage || 0).toLocaleString()} / ${Number(company.creditLimit).toLocaleString()}`
                              : "Unlimited"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.userCount} / {company.seats || 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.status || "active"}
                          size="small"
                          color={
                            company.status === "suspended"
                              ? "default"
                              : "primary"
                          }
                          variant={
                            company.status === "suspended"
                              ? "outlined"
                              : "filled"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.created_date
                            ? moment(company.created_date).format(
                                "MMM Do YYYY"
                              )
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleCompanyMenuClick(e, company)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {companies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 4 }}>
                          <Business
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" color="text.secondary">
                            No companies found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {companiesSearch
                              ? "Try adjusting your search terms"
                              : 'Create a company using the "New Company" button above'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {companiesTotal > companiesPageSize && (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={companiesTotal}
                  rowsPerPage={companiesPageSize}
                  page={companiesPage}
                  onPageChange={(_, p) => setCompaniesPage(p)}
                  onRowsPerPageChange={(e) => {
                    setCompaniesPageSize(parseInt(e.target.value, 10));
                    setCompaniesPage(0);
                  }}
                />
              )}
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Customer context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <VisibilityOutlined sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={handleRemoveCustomer}
          disabled={removeLoading}
          sx={{ color: "error.main" }}
        >
          <PersonRemove sx={{ mr: 1 }} />
          Remove Customer
        </MenuItem>
      </Menu>

      {/* Company context menu */}
      <Menu
        anchorEl={companyAnchorEl}
        open={Boolean(companyAnchorEl)}
        onClose={handleCompanyMenuClose}
      >
        <MenuItem onClick={handleEditCompany}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleManageCompanyUsers}>
          <Group sx={{ mr: 1 }} />
          Manage Users
        </MenuItem>
        <MenuItem onClick={handleToggleCompanyStatus}>
          {selectedCompany?.status === "suspended" ? (
            <>
              <PlayArrow sx={{ mr: 1 }} />
              Activate
            </>
          ) : (
            <>
              <Pause sx={{ mr: 1 }} />
              Suspend
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={handleDeleteCompany}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Organization context menu */}
      <Menu
        anchorEl={orgAnchorEl}
        open={Boolean(orgAnchorEl)}
        onClose={handleOrgMenuClose}
      >
        <MenuItem onClick={handleEditOrg}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleOrgStatus}>
          {selectedOrg?.status === "suspended" ? (
            <>
              <PlayArrow sx={{ mr: 1 }} />
              Activate
            </>
          ) : (
            <>
              <Pause sx={{ mr: 1 }} />
              Suspend
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={handleDeleteOrg}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {selectedCustomer && (
        <WhitelabelCustomerDetailDialog
          open={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedCustomer(null);
          }}
          customer={selectedCustomer}
          whitelabelId={whitelabelId}
          getToken={getToken}
        />
      )}

      <RemoveCustomerDialog
        open={removeDialogOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        customerEmail={customerToRemove?.email || ""}
        loading={removeLoading}
      />

      <CompanyFormDialog
        open={companyFormOpen}
        onClose={() => {
          setCompanyFormOpen(false);
          setEditCompany(null);
        }}
        onSaved={() => loadCompanies()}
        getToken={getToken as () => Promise<string>}
        whitelabelId={whitelabelId}
        editCompany={editCompany}
        organizations={organizations}
      />

      <OrganizationFormDialog
        open={orgFormOpen}
        onClose={() => {
          setOrgFormOpen(false);
          setEditOrg(null);
        }}
        onSaved={() => loadOrganizations()}
        getToken={getToken as () => Promise<string>}
        whitelabelId={whitelabelId}
        editOrganization={editOrg}
      />

      {companyForUsersDialog && (
        <CompanyUsersDialog
          open={companyUsersDialogOpen}
          onClose={() => {
            setCompanyUsersDialogOpen(false);
            setCompanyForUsersDialog(null);
          }}
          getToken={getToken as () => Promise<string>}
          whitelabelId={whitelabelId}
          companyId={companyForUsersDialog.id}
          companyName={companyForUsersDialog.name}
          users={companyForUsersDialog.users}
          seats={companyForUsersDialog.seats}
          onUsersChanged={() => loadCompanies()}
        />
      )}

      <GenerateReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        getToken={getToken as () => Promise<string>}
        whitelabelId={whitelabelId}
        companyProvisioningEnabled={companyProvisioningEnabled}
      />
    </Box>
  );
}
